import dayjs from 'dayjs'
import { PhoneNumberFormat, PhoneNumberUtil } from 'google-libphonenumber'
import { z } from 'zod'

import { patientGenderOptions } from '@/screens/velthuis/PatientDetails/utils.ts'

const phoneUtil = PhoneNumberUtil.getInstance()

const isValidPhoneNumber = (value: string) => {
  try {
    const phoneNumber = phoneUtil.parse(value, 'NL')
    return phoneUtil.isValidNumber(phoneNumber)
  } catch (error) {
    console.error(error)
    return false
  }
}

const formatDutchPhoneNumber = (value: string) => {
  try {
    const phoneNumber = phoneUtil.parse(value, 'NL')
    return phoneUtil.format(phoneNumber, PhoneNumberFormat.INTERNATIONAL)
  } catch (error) {
    console.error(error)
    return value
  }
}

const isValidZDCode = (value: string) => /^ZD\d{9}$/.test(value)

const phoneNumberSchema = z
  .string()
  .refine(isValidPhoneNumber, {
    message: 'Invalid phone number',
  })
  .transform(formatDutchPhoneNumber)
  .optional()

const contactSchema = z.object({
  email: z.string().email({ message: 'Invalid email address' }).optional(),
  id: z.string().optional(),
  phone: phoneNumberSchema,
})

type ContactData = z.infer<typeof contactSchema>

const dateOfBirthSchema = z
  .string()
  .min(1, { message: 'Date of birth is required' })
  .refine(
    (value) => {
      const age = dayjs().diff(dayjs(value), 'years')
      return age >= 0 && age <= 120
    },
    { message: 'Invalid date of birth' },
  )

const patientSchema = z.object({
  dateOfBirth: dateOfBirthSchema,
  email: z.string().email({ message: 'Invalid email address' }).optional(),
  firstName: z.string().min(1, { message: 'First name is required' }),
  gender: z.enum(patientGenderOptions),
  id: z.string().optional(),
  initials: z.string().min(1, { message: 'Initials are required' }),
  lastName: z.string().min(1, { message: 'Last name is required' }),
  phone: phoneNumberSchema,
})

const referralSchema = z.object({
  dateOfBirth: dateOfBirthSchema,
  patient: contactSchema.optional(),
  referralCode: z
    .string()
    .transform((val) => {
      return val.toUpperCase().replace(/\s+/g, '')
    })
    .refine(isValidZDCode, {
      message: "Referral code must be 'ZD' followed by 9 digits",
    }),
})

const bmiSchema = z.object({
  height: z.number().refine((val) => !isNaN(val) && val > 50 && val < 300, {
    message: 'Height is required',
  }),
  weight: z.number().refine((val) => !isNaN(val) && val > 10 && val < 500, {
    message: 'Weight is required',
  }),
})

type ReferralData = z.infer<typeof referralSchema>

export type { ContactData, ReferralData }
export { bmiSchema, contactSchema, patientSchema, referralSchema }
